
import React from 'react';
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Brain, GraduationCap, Target, Clock, Sparkles } from "lucide-react";
import { useGame } from '../../contexts/GameContext';
import { skills } from '../../data/skills';
import { motion } from "framer-motion";
import { getRandomChallengeForRole } from "../../utils/gameUtils";
import { toast } from "@/components/ui/sonner";

const SkillsAcquisitionGuide: React.FC = () => {
  const { 
    careerLevel, 
    experience, 
    skills: userSkills,
    selectedRole,
    yearsPassed,
    setCurrentChallenge,
    currentChallenge
  } = useGame();
  
  // Filter skills that are appropriate for the user's current level
  const availableSkills = skills.filter(skill => {
    // Don't show skills the user already has
    if (userSkills.some(userSkill => userSkill.id === skill.id)) {
      return false;
    }
    
    // Show skills appropriate for their level
    const levelOrder = { 'Junior': 1, 'Mid-Level': 2, 'Senior': 3, 'Leader': 4, 'Expert': 5 };
    const userLevelValue = levelOrder[careerLevel] || 1;
    
    // Scale required experience based on level
    const recommendedExperience = (skill.requiredExperience || 0) / userLevelValue;
    
    // Show skill if user's experience is at least 70% of what's typically required
    return experience >= recommendedExperience * 0.7;
  });

  // Group skills by category
  const groupedSkills = availableSkills.reduce((acc, skill) => {
    if (!acc[skill.category]) {
      acc[skill.category] = [];
    }
    acc[skill.category].push(skill);
    return acc;
  }, {} as Record<string, typeof skills>);

  const getCategoryGradient = (category: string) => {
    switch (category) {
      case 'Technical':
        return 'from-blue-500/20 to-blue-600/5';
      case 'Business':
        return 'from-amber-500/20 to-amber-600/5';
      case 'Operations':
        return 'from-green-500/20 to-green-600/5';
      case 'Leadership':
        return 'from-purple-500/20 to-purple-600/5';
      default:
        return 'from-web3-blue/20 to-web3-blue/5';
    }
  };

  // Function to handle the "Focus on Next Challenge" button click
  const handleFocusOnNextChallenge = () => {
    if (currentChallenge) {
      toast("A challenge is already active", {
        description: "Complete your current challenge before generating a new one.",
        duration: 3000,
      });
      return;
    }
    
    if (!selectedRole) {
      toast("Cannot generate challenge", {
        description: "You need to select a role first.",
        duration: 3000,
      });
      return;
    }
    
    // Generate a new random challenge based on role, level, skills, and years
    const newChallenge = getRandomChallengeForRole(
      selectedRole, 
      careerLevel,
      userSkills,
      yearsPassed
    );
    
    // Set the new challenge
    setCurrentChallenge(newChallenge);
    
    // Show a toast notification
    toast("New Challenge Generated!", {
      description: `${newChallenge.title} - Focus on this challenge to acquire new skills.`,
      duration: 4000,
    });
  };

  if (Object.keys(groupedSkills).length === 0) {
    return (
      <Card className="bg-gradient-to-br from-web3-blue/10 to-background border-web3-blue/20 shadow-lg hover:shadow-xl transition-all">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Brain className="mr-2 text-web3-green" /> Skills Acquisition Guide
          </CardTitle>
          <CardDescription>Learn how to acquire new skills</CardDescription>
        </CardHeader>
        <CardContent>
          <motion.div 
            className="text-center py-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ 
                delay: 0.2,
                type: "spring",
                stiffness: 100,
                damping: 10
              }}
            >
              <GraduationCap className="mx-auto h-12 w-12 text-muted-foreground mb-3" />
            </motion.div>
            <h3 className="text-lg font-medium mb-2">No New Skills Available</h3>
            <p className="text-muted-foreground">
              Complete more challenges or advance your career level to unlock new skills.
            </p>
          </motion.div>
          
          <div className="mt-4 text-center">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              <Button 
                variant="outline" 
                size="sm" 
                className="bg-gradient-to-r from-web3-blue/20 to-web3-green/20 border-web3-blue/30 hover:border-web3-green/50"
                onClick={handleFocusOnNextChallenge}
              >
                <GraduationCap className="mr-2 h-4 w-4" /> Focus on Next Challenge
              </Button>
            </motion.div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-web3-blue/10 to-background border-web3-blue/20 shadow-lg hover:shadow-xl transition-all">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Brain className="mr-2 text-web3-green" /> Skills Acquisition Guide
        </CardTitle>
        <CardDescription>Learn how to acquire new skills</CardDescription>
      </CardHeader>
      <CardContent>
        <motion.div 
          className="mb-4 p-3 bg-web3-blue/20 rounded-md backdrop-blur-sm"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <h3 className="text-sm font-medium mb-2 flex items-center">
            <Target className="h-4 w-4 mr-2 text-web3-green" /> 
            How to Acquire Skills
          </h3>
          <p className="text-sm text-white/80">
            Skills are acquired through making strategic decisions when facing challenges. 
            Each challenge offers different options that can unlock specific skills.
            Focus on challenges that align with your career goals.
          </p>
        </motion.div>

        <ScrollArea className="h-[300px] pr-4">
          {Object.entries(groupedSkills).map(([category, categorySkills], categoryIndex) => (
            <motion.div 
              key={category} 
              className="mb-6 last:mb-0"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: categoryIndex * 0.1 }}
            >
              <h3 className="text-lg font-medium mb-3 text-web3-green flex items-center">
                <Sparkles className="mr-2 h-4 w-4" />
                {category} Skills
              </h3>
              <div className="space-y-3">
                {categorySkills.map((skill, index) => (
                  <motion.div 
                    key={skill.id} 
                    className={`border border-web3-blue/30 rounded-lg p-3 bg-gradient-to-br ${getCategoryGradient(category)} backdrop-blur-sm hover:shadow-md hover:-translate-y-0.5 transition-all`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 * index + 0.2 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex justify-between items-start">
                      <h4 className="font-medium text-white">{skill.name}</h4>
                      <div className="px-2 py-1 text-xs rounded-full bg-web3-blue/20 text-web3-blue">
                        {skill.category}
                      </div>
                    </div>
                    <p className="text-sm text-white/80 mt-1">{skill.description}</p>
                    
                    <div className="mt-3 flex items-center text-xs text-white/60">
                      <Clock className="h-3 w-3 mr-1" />
                      <span>
                        Typically unlocked through {skill.category === 'Technical' ? 'technical challenges' : 
                          skill.category === 'Business' ? 'business decisions' : 
                          skill.category === 'Operations' ? 'operational improvements' : 
                          'leadership challenges'}
                      </span>
                    </div>
                    
                    {skill.requiredExperience && (
                      <div className="mt-2 w-full bg-black/20 h-1 rounded-full overflow-hidden">
                        <motion.div 
                          className="h-full bg-web3-blue"
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min(100, (experience / skill.requiredExperience) * 100)}%` }}
                          transition={{ duration: 1, delay: 0.2 * index }}
                        />
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </ScrollArea>
        
        <div className="mt-4 text-center">
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
          >
            <Button 
              variant="outline" 
              size="sm" 
              className="bg-gradient-to-r from-web3-blue/20 to-web3-green/20 border-web3-blue/30 hover:border-web3-green/50"
              onClick={handleFocusOnNextChallenge}
            >
              <GraduationCap className="mr-2 h-4 w-4" /> Focus on Next Challenge
            </Button>
          </motion.div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SkillsAcquisitionGuide;
